import { Button } from "@fieldwork/ui/components/button";
import { Input } from "@fieldwork/ui/components/input";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { type ReactNode, useState } from "react";
import { toast } from "sonner";

import { useServer } from "@/components/server-hooks";
import { Note, Panel, ServerPage } from "@/components/server-shell";
import { ago, Chip, errorMessage, Select } from "@/components/ui-bits";
import { revealKey } from "@/lib/seal";
import { client, orpc } from "@/utils/orpc";

export const Route = createFileRoute("/_auth/servers/$id/server")({
  component: ServerSettings,
});

const TIMERS: [string, string][] = [
  ["task-manager-ai", "15-minute cycle"],
  ["task-manager-ai-sweep", "Morning sweep"],
  ["task-manager-ai-briefing", "Morning briefing"],
  ["task-manager-ai-health", "Health check"],
  ["task-manager-ai-context-commit", "Context backup"],
  ["task-manager-ai-inbox-scan", "Inbox scan"],
];

const gb = (n?: number) => (n ? `${Math.round(n / 1024 ** 3)} GB` : "?");
const days = (s?: number | null) => (s ? (s >= 86400 ? `up ${Math.floor(s / 86400)} days` : `up ${Math.max(1, Math.floor(s / 3600))} h`) : "");

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5 bg-[#1A1A19] px-[18px] py-3.5">
      <span className="font-mono text-[11px] uppercase tracking-[0.08em] text-[#A8A59D]">{label}</span>
      {children}
    </div>
  );
}
const Sub = ({ children }: { children: ReactNode }) => <span className="font-mono text-xs text-[#A8A59D]">{children}</span>;

function ServerSettings() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const q = useServer(id);
  const s = q.data?.server;
  const st = q.data?.hub.status;
  const connected = !!q.data?.hub.connected;
  const [confirmName, setConfirmName] = useState("");
  // Hetzner tokens are pasted per action and never stored (see the delete panel and restart)
  const [hzToken, setHzToken] = useState("");
  const [panelOnly, setPanelOnly] = useState(false);

  const refresh = useMutation({
    mutationFn: () => client.servers.refresh({ id }),
    onSuccess: () => q.refetch(),
    onError: (e) => toast.error(errorMessage(e)),
  });
  const runCycle = useMutation({
    mutationFn: () => client.servers.runCycle({ id }),
    onSuccess: () => toast.success("Cycle started"),
    onError: (e) => toast.error(errorMessage(e)),
  });
  const reboot = useMutation({
    mutationFn: (token: string) => client.servers.restart({ id, token }),
    onSuccess: () => toast.success("Restarting. The server reconnects in a minute or two."),
    onError: (e) => toast.error(errorMessage(e)),
  });
  const del = useMutation({
    mutationFn: () => client.servers.delete(panelOnly ? { id, panelOnly: true } : { id, token: hzToken.trim() || undefined }),
    onSuccess: () => {
      toast.success("Server deleted");
      navigate({ to: "/servers" });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  if (!s)
    return (
      <ServerPage eyebrow="Settings" title="Server">
        <p className="text-sm text-[#A8A59D]">Loading…</p>
      </ServerPage>
    );

  const install = st?.install;
  const diskUsed = st?.disk ? 1 - st.disk.free / st.disk.total : 0;
  return (
    <ServerPage
      maxWidth={1000}
      eyebrow="Settings"
      title={
        <>
          Server <Chip value={connected ? "online" : "offline"} label={connected ? "online" : "offline"} />
        </>
      }
      actions={
        <>
          <Button variant="outline" disabled={!connected || refresh.isPending} onClick={() => refresh.mutate()}>
            Refresh
          </Button>
          <Button variant="outline" disabled={!connected || runCycle.isPending} onClick={() => runCycle.mutate()}>
            Run a cycle now
          </Button>
          {s.provider === "hetzner" && s.status !== "creating" && (
            <Button
              variant="outline"
              disabled={reboot.isPending}
              onClick={() => {
                const token = window.prompt(
                  `Restart ${s.name}? It is offline for a minute or two.\n\nPaste a Hetzner API token (Read & Write). It is used for this restart only and not saved.`,
                );
                if (token?.trim()) reboot.mutate(token.trim());
              }}
            >
              Restart server
            </Button>
          )}
        </>
      }
    >
      {s.error && (
        <p className="m-0 rounded-md bg-[rgba(240,100,90,0.08)] px-4 py-3 text-sm text-[#F48A80] shadow-[inset_0_0_0_1px_rgba(240,100,90,0.35)]">
          {s.error}
        </p>
      )}

      <section aria-label="Server facts" className="grid grid-cols-[repeat(auto-fit,minmax(min(200px,100%),1fr))] gap-px overflow-hidden rounded-md bg-[#2A2927]">
        <Fact label="Where">
          <span>{s.provider === "hetzner" ? `Hetzner ${s.serverType ?? ""} · ${s.location ?? ""}` : "Connected machine"}</span>
          <Sub>{s.ipv4 ?? st?.hostname ?? s.hostname ?? ""}</Sub>
        </Fact>
        <Fact label="Agent">
          <span className={connected ? "text-[#7FD39B]" : "text-[#F0B65A]"}>
            {connected ? `connected ${ago(q.data?.hub.connectedAt)}` : `last seen ${ago(s.lastSeenAt)}`}
          </span>
          <Sub>{days(st?.uptime_s)}</Sub>
        </Fact>
        <Fact label="Disk">
          <span>{st?.disk ? `${gb(st.disk.free)} free of ${gb(st.disk.total)}` : "?"}</span>
          <div aria-hidden="true" className="mt-1.5 h-1 rounded-sm bg-[#2A2927]">
            <div className="h-1 rounded-sm bg-[#ECEAE4]" style={{ width: `${Math.round(diskUsed * 100)}%` }} />
          </div>
        </Fact>
        <Fact label="Memory · load">
          <span>{st?.memory ? `${gb(st.memory.MemAvailable)} free of ${gb(st.memory.MemTotal)}` : "?"}</span>
          <Sub>{st?.load?.map((l) => l.toFixed(2)).join(" ") ?? ""}</Sub>
        </Fact>
      </section>

      {s.status === "booting" && !connected && (
        <Note>Waiting for the server to start and connect. A new Hetzner server usually connects within 2–3 minutes.</Note>
      )}

      {install && install.state !== "installed" && (
        <Panel title="Install" className="px-[22px] py-5" badge={<Chip value={install.state} />}>
          {install.step && <Note>{install.step}</Note>}
          <LogBox>{(install.tail ?? []).join("\n")}</LogBox>
        </Panel>
      )}

      {connected && <PlaneSection id={id} />}

      {st?.units && (
        <section aria-labelledby="sv-sched" className="rounded-md bg-[#1A1A19] shadow-[inset_0_0_0_1px_#2A2927]">
          <h2 id="sv-sched" className="m-0 px-[22px] pt-[18px] pb-2.5 font-mono text-base font-bold">
            Schedules
          </h2>
          {TIMERS.map(([t, label]) => {
            const timer = st.units?.[`${t}.timer`];
            if (!timer) return null;
            const svc = st.units?.[`${t}.service`] ?? {};
            const on = timer.ActiveState === "active";
            const next = timer.NextElapseUSecRealtime;
            return (
              <div key={t} className="flex flex-wrap items-center gap-x-3.5 gap-y-2 border-t border-[#2A2927] px-[22px] py-3">
                <Chip value={on ? "ok" : "warn"} label={on ? "on" : (timer.ActiveState ?? "off")} />
                <span className="flex-[1_1_200px]">{label}</span>
                <span className="flex items-center gap-2">
                  {svc.ActiveState === "activating" || svc.ActiveState === "active" ? (
                    <Chip value="info" label="running" />
                  ) : (
                    svc.Result && svc.Result !== "success" && <Chip value="error" label="last run failed" />
                  )}
                  <span className="text-[13px] text-[#A8A59D]">
                    {next
                      ? `next ${next}`
                      : // relative timers (the cycle) have no clock time: they run 15 min after the last run ends
                        `last ran ${timer.LastTriggerUSec && timer.LastTriggerUSec !== "n/a" ? timer.LastTriggerUSec : "–"} · then every 15 min after it finishes`}
                  </span>
                </span>
              </div>
            );
          })}
        </section>
      )}

      {connected && <Logs id={id} />}

      <Activity id={id} />

      {connected && (
        <Panel title="Updates" className="gap-1 px-[22px] py-5 [&>div:first-child]:mb-2.5">
          <AgentUpdate id={id} version={st?.agent_version ?? s.agentVersion ?? "?"} />
          <TmaiUpdate id={id} version={st?.tmai_version ?? s.tmaiVersion ?? null} git={!!st?.tmai_git} />
        </Panel>
      )}

      <Panel danger title="Delete this server" className="gap-3 px-[22px] py-5">
        <p className="m-0 text-[#CFCBC1]">
          {s.provider === "hetzner"
            ? "Deletes the machine and its firewall in your Hetzner project. Everything on it, including emails, tasks and Plane, is gone for good."
            : "Disconnects this machine from the panel. The machine and Fieldwork keep running; remove the agent there yourself."}
        </p>
        {s.provider === "hetzner" && (
          <div className="flex flex-col gap-2">
            <label className="flex max-w-[480px] flex-col gap-1.5">
              <span className="font-mono text-[11px] uppercase tracking-[0.08em] text-[#A8A59D]">
                Hetzner API token (Read &amp; Write), used once, not saved
              </span>
              <Input
                className="h-11 font-mono"
                type="password"
                autoComplete="off"
                disabled={panelOnly}
                value={hzToken}
                onChange={(e) => setHzToken(e.target.value)}
              />
            </label>
            <label className="flex items-center gap-2 text-[13px] text-[#CFCBC1]">
              <input type="checkbox" className="size-4 accent-[#FF4F1A]" checked={panelOnly} onChange={(e) => setPanelOnly(e.target.checked)} />
              I already deleted it in the Hetzner console: only remove it from the panel
            </label>
          </div>
        )}
        <form
          className="flex flex-wrap items-end gap-2.5"
          onSubmit={(e) => {
            e.preventDefault();
            if (confirmName === s.name) del.mutate();
          }}
        >
          <label className="flex flex-[0_1_320px] flex-col gap-1.5">
            <span className="font-mono text-[11px] uppercase tracking-[0.08em] text-[#A8A59D]">
              Type <span className="text-[#ECEAE4] normal-case">{s.name}</span> to confirm
            </span>
            <Input className="h-11 font-mono" autoComplete="off" value={confirmName} onChange={(e) => setConfirmName(e.target.value)} />
          </label>
          <Button
            type="submit"
            variant="destructive"
            size="lg"
            disabled={confirmName !== s.name || del.isPending || (s.provider === "hetzner" && !panelOnly && hzToken.trim().length < 20)}
          >
            {del.isPending ? "Deleting…" : "Delete server"}
          </Button>
        </form>
      </Panel>
    </ServerPage>
  );
}

function LogBox({ children }: { children: ReactNode }) {
  return (
    <pre className="m-0 max-h-[300px] overflow-auto rounded-[4px] bg-[#0E0E0D] px-4 py-3.5 font-mono text-xs leading-[1.7] whitespace-pre-wrap text-[#CFCBC1]">
      {children}
    </pre>
  );
}

function UpdateRow({ name, version, note, children }: { name: string; version: ReactNode; note: string; children: ReactNode }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-2.5 border-t border-[#2A2927] py-3">
      <span className="flex flex-col">
        <span>
          <span className="font-medium">{name}</span> <span className="font-mono text-[13px] text-[#A8A59D]">{version}</span>
        </span>
        <span className="text-[13px] text-[#A8A59D]">{note}</span>
      </span>
      {children}
    </div>
  );
}

function TmaiUpdate({ id, version, git }: { id: string; version: string | null; git: boolean }) {
  const update = useMutation({
    mutationFn: () => client.servers.updateTmai({ id }),
    onSuccess: (r) => toast.success(`Fieldwork engine ${r.from ?? "?"} → ${r.to ?? "?"}. ${r.notes.join(" · ")}`),
    onError: (e) => toast.error(errorMessage(e)),
  });
  if (git)
    return (
      <UpdateRow
        name="Fieldwork engine"
        version={`git checkout · ${version ?? "?"}`}
        note="This server runs the engine from a git checkout, so the panel doesn't install releases over it. Update it with git pull on the server."
      >
        <span />
      </UpdateRow>
    );
  return (
    <UpdateRow
      name="Fieldwork engine"
      version={version ?? "version unknown"}
      note="Installs the panel's current release. Code only: settings, accounts, tasks and context stay. Waits for a running cycle and keeps a backup."
    >
      <Button
        variant="outline"
        disabled={update.isPending}
        onClick={() => window.confirm("Update the Fieldwork engine on this server?") && update.mutate()}
      >
        {update.isPending ? "Updating…" : "Update"}
      </Button>
    </UpdateRow>
  );
}

function AgentUpdate({ id, version }: { id: string; version: string }) {
  const update = useMutation({
    mutationFn: () => client.servers.updateAgent({ id }),
    onSuccess: (r) => toast.success(`Agent updated (${r.installed}); it reconnects in a few seconds.`),
    onError: (e) => toast.error(errorMessage(e)),
  });
  return (
    <UpdateRow name="Fieldwork agent" version={version} note="Installs the panel's current agent; the engine is not changed.">
      <Button variant="outline" disabled={update.isPending} onClick={() => update.mutate()}>
        {update.isPending ? "Updating…" : "Update"}
      </Button>
    </UpdateRow>
  );
}

function PlaneSection({ id }: { id: string }) {
  const status = useQuery({
    ...orpc.servers.planeStatus.queryOptions({ input: { id } }),
    retry: false,
    refetchOnWindowFocus: false,
  });
  const setup = useMutation({
    mutationFn: () => client.servers.planeSetup({ id }),
    onSuccess: () => {
      toast.success("Plane is ready");
      status.refetch();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const open = useMutation({
    mutationFn: () => client.servers.planeLink({ id }),
    onSuccess: ({ url }) => window.open(url, "_blank", "noopener"),
    onError: (e) => toast.error(errorMessage(e)),
  });
  const login = useMutation({
    mutationFn: async () => {
      const k = await revealKey();
      const { sealed } = await client.servers.planeLogin({ id, to: k.publicKey });
      return JSON.parse(await k.open(sealed, id, "plane_login")) as { email: string; password: string };
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const st = status.data;
  const ready = !!(st?.installed && st.ready_for && !setup.isPending);
  const starting = !!(st?.installed && st.configured && !st.api_ok && !setup.isPending);
  const needsSetup = !!(st?.installed && (!st.configured || setup.isPending));
  return (
    <Panel
      className="px-[22px] py-5"
      title="Plane, your task tracker"
      badge={
        st &&
        (ready ? <Chip value="ok" label="ready" /> : starting ? <Chip value="info" label="starting" /> : needsSetup ? <Chip value="warn" label="needs setup" /> : !st.installed ? <Chip value="neutral" label="not installed" /> : null)
      }
      actions={
        ready && (
          <>
            <Button
              variant="ghost"
              disabled={setup.isPending}
              onClick={() => window.confirm("Run Plane setup again? Plane restarts (about a minute).") && setup.mutate()}
            >
              Run setup again
            </Button>
            <Button disabled={open.isPending} onClick={() => open.mutate()}>
              Open Plane ↗
            </Button>
          </>
        )
      }
    >
      {status.error && <p className="m-0 text-sm text-[#F48A80]">{status.error.message}</p>}
      {st && !st.installed && <Note>Plane is not installed on this server.</Note>}
      {st?.installed && (
        <div className="grid grid-cols-[repeat(auto-fit,minmax(min(260px,100%),1fr))] gap-3 rounded-md bg-[#111111] px-4 py-3.5">
          <div className="flex flex-col gap-1">
            <span className="font-mono text-[11px] uppercase tracking-[0.08em] text-[#A8A59D]">Plane login</span>
            <span className="font-mono select-all">{login.data?.email ?? "hidden"}</span>
          </div>
          <div className="flex flex-col gap-1">
            <span className="font-mono text-[11px] uppercase tracking-[0.08em] text-[#A8A59D]">Password</span>
            <span className="flex flex-wrap items-center gap-2.5">
              <span className="font-mono tracking-[0.15em] select-all">{login.data ? login.data.password : "••••••••••••"}</span>
              <Button size="xs" variant="outline" disabled={login.isPending} onClick={() => (login.data ? login.reset() : login.mutate())}>
                {login.data ? "Hide" : "Show"}
              </Button>
              {login.data && (
                <Button
                  size="xs"
                  variant="outline"
                  onClick={() => navigator.clipboard.writeText(login.data.password).then(() => toast.success("Copied"))}
                >
                  Copy
                </Button>
              )}
            </span>
          </div>
        </div>
      )}
      {st?.installed && <Note>Generated on your server at install. If you change it in Plane, this keeps showing the original.</Note>}
      {starting && (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Note>Plane is starting up (it takes a minute or two after a restart).</Note>
          <Button variant="outline" onClick={() => status.refetch()}>
            Check again
          </Button>
        </div>
      )}
      {needsSetup && (
        <>
          <Note>
            To open Plane from here, the server sets Plane's address to this panel's Plane address, switches off open sign-up,
            and restarts Plane. It takes about a minute; nothing else changes.
          </Note>
          <div>
            <Button disabled={setup.isPending} onClick={() => setup.mutate()}>
              {setup.isPending ? "Setting up… (about a minute)" : "Set up Plane access"}
            </Button>
          </div>
          {setup.data && <LogBox>{setup.data.steps.join("\n")}</LogBox>}
        </>
      )}
    </Panel>
  );
}

/** The panel's audit log for this server: every action taken on it through the panel, by whom. */
function Activity({ id }: { id: string }) {
  const [open, setOpen] = useState(false);
  const q = useQuery({ ...orpc.servers.activity.queryOptions({ input: { id } }), enabled: open, retry: false });
  return (
    <Panel
      className="gap-3 px-[22px] py-5"
      title="Panel activity"
      actions={
        <Button size="sm" variant="outline" className="text-xs" onClick={() => (open ? q.refetch() : setOpen(true))}>
          {open ? "Reload" : "Show"}
        </Button>
      }
    >
      <Note>Everything done to this server through the panel, newest first: who did it, when, and from which IP address.</Note>
      {open && q.error && <p className="m-0 text-sm text-[#F48A80]">{q.error.message}</p>}
      {open && q.data && q.data.length === 0 && <p className="m-0 text-sm text-[#A8A59D]">Nothing yet.</p>}
      {open && q.data && q.data.length > 0 && (
        <ul className="m-0 flex list-none flex-col gap-1 p-0 font-mono text-[12px]">
          {q.data.map((a, i) => (
            <li key={`${a.at}-${i}`} className="flex flex-wrap gap-x-3 text-[#CFCBC1]">
              <span className="text-[#A8A59D]" title={new Date(a.at).toLocaleString()}>
                {ago(a.at)}
              </span>
              <span>{a.action.replace(/^server\./, "").replace(/_/g, " ")}</span>
              <span className={a.by === "you" ? "text-[#A8A59D]" : "text-[#F0B65A]"}>by {a.by}</span>
              {a.ip && <span className="text-[#6F6C66]">{a.ip}</span>}
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

function Logs({ id }: { id: string }) {
  const [source, setSource] = useState<"cycle" | "install" | "journal">("cycle");
  const [unit, setUnit] = useState("task-manager-ai.service");
  const logs = useQuery({
    ...orpc.servers.logs.queryOptions({
      input: { id, source, unit: source === "journal" ? unit : undefined, lines: 200 },
    }),
    retry: false,
  });
  return (
    <Panel
      className="gap-3 px-[22px] py-5"
      title="Logs"
      actions={
        <>
          <label className="flex">
            <span className="sr-only">Log</span>
            <Select className="h-9 w-auto text-[13px]" value={source} onChange={(e) => setSource(e.target.value as typeof source)}>
              <option value="cycle">Cycle log</option>
              <option value="install">Install log</option>
              <option value="journal">System journal</option>
            </Select>
          </label>
          {source === "journal" && (
            <label className="flex">
              <span className="sr-only">Unit</span>
              <Select className="h-9 w-auto text-[13px]" value={unit} onChange={(e) => setUnit(e.target.value)}>
                {[...TIMERS.map(([t, label]) => [`${t}.service`, label]), ["fieldwork-agent.service", "Fieldwork agent"]].map(([u, label]) => (
                  <option key={u} value={u}>
                    {label}
                  </option>
                ))}
              </Select>
            </label>
          )}
          <Button size="sm" variant="outline" className="text-xs" onClick={() => logs.refetch()}>
            Reload
          </Button>
        </>
      }
    >
      <LogBox>{logs.error ? logs.error.message : (logs.data?.lines ?? []).join("\n") || "(empty)"}</LogBox>
    </Panel>
  );
}
